import { useDropzone } from 'react-dropzone'
import { Text, TextProps } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Upload5 } from 'blaze-ui/icons'
import Styles from './Dropzone.module.scss'
import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { FC, PropsWithChildren, useRef } from 'react'
import { ReactComponent as Files } from './files.svg'
import { ReactComponent as DropFiles } from './drop-files.svg'
import { ButtonProps } from 'blaze-ui/Button/Types'

interface OverlayProps {
  DragOverlayComponent?: FC<{ isActive: boolean }>
}

interface Props
  extends OverlayProps,
    PropsWithChildren<{
      onDrop: (files: File[]) => void
      accept?: string
      supportText?: string
      styles?: Record<string, string> | string
      IconComponent?: FC
      buttonVariant?: ButtonProps['variant']
      buttonText?: string
      buttonSize?: ButtonProps['size']
      supportTextColor?: string
      supportTextVariant?: TextProps['variant']
      multiple?: boolean
    }> {}

export const DragOverlay = ({ isActive, children }: PropsWithChildren<{ isActive: boolean }>) => (
  <div className={cx(Styles.dropOverlay, { [Styles.dragActive]: isActive })} data-theme="dark">
    <div className={Styles.details}>
      {children}
      {!children && <Files />}
      <Heading level={1}>Drop your files here</Heading>
      <Heading level={5}>And let’s get working on your project!</Heading>
    </div>
  </div>
)

export const Dropzone = ({
  onDrop,
  accept,
  supportText = 'Blaze supports uploading images and videos',
  styles = {},
  IconComponent = DropFiles,
  DragOverlayComponent = DragOverlay,
  buttonVariant = 'primary',
  children,
  buttonText = 'Choose Files',
  buttonSize = 'md',
  supportTextColor = 'var(--dark-40)',
  supportTextVariant = 'metadata',
  multiple = true,
}: Props) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, multiple })
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div {...getRootProps()} className={cx(Styles.dropzone, styles)} data-testid="upload-dropzone">
      <input {...getInputProps()} ref={inputRef} aria-label="Upload files" />
      <DragOverlayComponent isActive={isDragActive} />
      {!isDragActive && (
        <>
          <div className={Styles.details}>
            <IconComponent />
            {children || <Text variant="secondary">Drop files here or</Text>}
            <Button
              variant={buttonVariant}
              frontIcon={Upload5}
              onPress={() => inputRef.current?.click()}
              size={buttonSize}
            >
              {buttonText}
            </Button>
          </div>
          {supportText && (
            <div className={Styles.supportText}>
              <Text variant={supportTextVariant} color={supportTextColor}>
                {supportText}
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  )
}
